import React, { useState, useEffect, useContext } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  InputAdornment
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { LoadingContext } from 'contexts/LoadingContext'
import { requestPasswordReset, confirmPasswordReset } from 'api/auth'
import PasswordFields from 'pages/ActivateAccount/PasswordFields'
import LockResetIcon from '@mui/icons-material/LockReset'

interface EditPasswordFormValues {
  code: string
  password: string
  confirmPassword: string
}

type EditPasswordProps = {
  onCancel: () => void
}

const EditPasswordForm: React.FC<EditPasswordProps> = ({ onCancel }) => {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<EditPasswordFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur'
  })

  const { showMessage } = useContext(SnackbarContext)
  const { showLoading, stopLoading } = useContext(LoadingContext)

  const [isResendDisabled, setIsResendDisabled] = useState(false)
  const [resendLabel, setResendLabel] = useState('Send')
  const [timer, setTimer] = useState(0)

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Initialize resend label
  useEffect(() => {
    setResendLabel(t('myAccount.editPassword.send'))
  }, [t])

  useEffect(() => {
    let countdown: NodeJS.Timeout
    if (timer > 0) {
      countdown = setTimeout(() => {
        setTimer(prev => prev - 1)
      }, 1000)
    } else if (timer === 0 && isResendDisabled) {
      setIsResendDisabled(false)
      setResendLabel(t('myAccount.editPassword.resend'))
    }
    return () => clearTimeout(countdown)
  }, [timer, isResendDisabled, t])

  const handleSendVerificationCode = async () => {
    setIsResendDisabled(true)
    setTimer(30)

    const result = await requestPasswordReset()
    if (result.success) {
      showMessage(t('snackbar.passwordReset.codeRequested'))
    } else {
      showMessage(result.error, 'error')
    }
  }

  const onSubmit = async (data: EditPasswordFormValues) => {
    showLoading()
    const result = await confirmPasswordReset(data.code, data.password)
    stopLoading()
    if (result.success) {
      showMessage(t('snackbar.passwordReset.resetSuccess'))
      onCancel()
    } else {
      showMessage(result.error, 'error')
    }
  }

  return (
    <Paper
      elevation={3}
      sx={{
        padding: 4,
        maxWidth: 500,
        margin: 'auto',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          mb: 3
        }}
      >
        <LockResetIcon
          sx={{
            fontSize: 32,
            color: 'primary.main'
          }}
        />
        <Typography
          variant='h4'
          sx={{
            fontWeight: 700,
            color: 'primary.main'
          }}
        >
          {t('myAccount.editPassword.title')}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              label={t('myAccount.editPassword.verificationCode')}
              variant='outlined'
              fullWidth
              required
              autoComplete='one-time-code'
              {...register('code', {
                required: t('myAccount.editPassword.errors.codeRequired')
              })}
              onChange={e =>
                setValue('code', e.target.value.replace(/\s/g, ''))
              }
              error={!!errors.code}
              helperText={errors.code?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <Button
                      onClick={handleSendVerificationCode}
                      disabled={isResendDisabled}
                    >
                      {resendLabel}
                    </Button>
                  </InputAdornment>
                )
              }}
            />
            {isResendDisabled && (
              <Typography
                variant='body2'
                sx={{
                  mt: 1,
                  color: 'text.secondary',
                  fontSize: '0.875rem'
                }}
              >
                {t('myAccount.editPassword.secondsLeft', { seconds: timer })}
              </Typography>
            )}
          </Grid>

          <PasswordFields
            register={register}
            errors={errors}
            watch={watch}
            setValue={setValue}
            showPassword={showPassword}
            showConfirmPassword={showConfirmPassword}
            toggleShowPassword={() => setShowPassword(prev => !prev)}
            toggleShowConfirmPassword={() =>
              setShowConfirmPassword(prev => !prev)
            }
          />
        </Grid>

        <Box display='flex' gap={2} sx={{ mt: 4 }}>
          <Button
            variant='outlined'
            fullWidth
            color='primary'
            onClick={onCancel}
            sx={{
              py: 1,
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 400
            }}
          >
            {t('myAccount.editPassword.cancel')}
          </Button>
          <Button
            variant='contained'
            fullWidth
            color='primary'
            type='submit'
            sx={{
              py: 1,
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 400
            }}
          >
            {t('myAccount.editPassword.save')}
          </Button>
        </Box>
      </form>
    </Paper>
  )
}

export default EditPasswordForm
